import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getChemicalDashboard, deleteChemical, restoreChemical, reorderChemicals, updateChemical, refreshAllPrices, updateChemicalChange, deleteChemicalChange, getAllChemicalChanges, uploadChemicalSds, downloadChemicalSds, deleteChemicalSds, fetchChemicalSdsFromSupplier, exportSdsBinder } from '../services/chemicalService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

// A soap used by both the SS bays and the auto wash exists as one row per
// system, but both draw from a single shelf: quantity_on_hand is synced
// across the rows rather than split, and the two systems deplete that stock
// simultaneously. So the rows have to be collapsed before any coverage or
// ordering math — otherwise the stock is counted once per system and each
// system's (individually slow) usage rate makes the supply look far
// longer-lasting than it is.
//
// Rates add rather than average: a jug feeding two systems at 1/400 and
// 1/400 jugs-per-day empties in 200 days, not 400.
const combineSharedSoaps = (chemicals) => {
  const out = [];
  const seen = new Set();
  for (const stats of chemicals) {
    const name = stats.chemical.name;
    if (seen.has(name)) continue;
    seen.add(name);

    const entries = chemicals.filter((s) => s.chemical.name === name);
    const rates = entries.map((e) => e.avg_days_per_jug).filter((d) => d && d > 0);
    const daysPerJug = rates.length
      ? 1 / rates.reduce((sum, d) => sum + 1 / d, 0)
      : null;
    const qty = entries[0].chemical.quantity_on_hand || 0;

    out.push({
      key: name,
      name,
      entries,
      ids: entries.map((e) => e.chemical.id),
      system: entries.length > 1
        ? entries.map((e) => (e.chemical.location === 'SS' ? 'SS Bays' : 'Auto Wash')).join(' + ')
        : (entries[0].chemical.location === 'SS' ? 'SS Bays' : 'Auto Wash'),
      size: entries[0].chemical.container_size_gallons,
      costPer: entries[0].chemical.cost_per_container,
      qty,
      daysPerJug,
      daysSupply: daysPerJug && qty > 0 ? qty * daysPerJug : null,
      productUrl: entries[0].chemical.product_url || '',
    });
  }
  return out;
};

const ChemicalDashboard = () => {
  const { currentLocation } = useLocationContext();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderDays, setOrderDays] = useState(180);
  const [sortMode, setSortMode] = useState('manual'); // 'manual', 'days_remaining', 'name'
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [retireTarget, setRetireTarget] = useState(null);
  const [retireDate, setRetireDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [retiring, setRetiring] = useState(false);
  const [sdsModalChem, setSdsModalChem] = useState(null);
  const [sdsBusy, setSdsBusy] = useState(false);
  const [sdsError, setSdsError] = useState('');
  const [sdsViewer, setSdsViewer] = useState(null); // { url, filename, name }
  const [binderBusy, setBinderBusy] = useState(false);

  useEffect(() => {
    fetchDashboard(true);
  }, [currentLocation?.id]);

  const fetchDashboard = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      const data = await getChemicalDashboard();
      setDashboard(data);
    } catch (err) {
      setError('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPrices = async () => {
    try {
      setRefreshing(true);
      setRefreshMsg('');
      const result = await refreshAllPrices();
      const parts = [];
      if (result.total_updated > 0) {
        parts.push(`${result.total_updated} price${result.total_updated !== 1 ? 's' : ''} updated`);
      }
      if (result.failed.length > 0) {
        parts.push(`${result.failed.length} failed`);
      }
      if (result.total_updated === 0 && result.failed.length === 0) {
        parts.push(`All ${result.total_checked} prices are current`);
      }
      setRefreshMsg(parts.join(', '));
      if (result.total_updated > 0) fetchDashboard();
      setTimeout(() => setRefreshMsg(''), 8000);
    } catch {
      setRefreshMsg('Failed to refresh prices');
      setTimeout(() => setRefreshMsg(''), 5000);
    } finally {
      setRefreshing(false);
    }
  };

  // Retiring asks for the date the soap actually came out of service rather
  // than assuming today — soap cost stops accruing on that date, so a soap
  // archived long after the fact would otherwise keep billing the months in
  // between.
  const handleDelete = (chemId, chemName) => {
    setRetireDate(new Date().toISOString().split('T')[0]);
    setRetireTarget({ id: chemId, name: chemName });
  };

  const confirmRetire = async () => {
    if (!retireTarget) return;
    const { id, name } = retireTarget;
    try {
      setRetiring(true);
      const res = await deleteChemical(id, retireDate);
      setRetireTarget(null);
      setRefreshMsg(
        res?.archived
          ? `${name} archived as of ${new Date(retireDate + 'T12:00:00').toLocaleDateString()} — change history kept.`
          : `${name} deleted.`
      );
      setTimeout(() => setRefreshMsg(''), 6000);
      fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove chemical');
      setRetireTarget(null);
    } finally {
      setRetiring(false);
    }
  };

  const handleRestore = async (chemId, chemName) => {
    try {
      await restoreChemical(chemId);
      setRefreshMsg(`${chemName} restored to the active slate.`);
      setTimeout(() => setRefreshMsg(''), 6000);
      fetchDashboard();
    } catch (err) {
      setError('Failed to restore chemical');
    }
  };

  const handleSdsView = async (chem) => {
    try {
      const res = await downloadChemicalSds(chem.id);
      const filename = chem.sds_filename || `${chem.name.replace(/\s+/g, '_')}_SDS.pdf`;
      const url = window.URL.createObjectURL(res.data);
      setSdsViewer({ url, filename, name: chem.name });
    } catch (err) {
      setError('Failed to load SDS');
    }
  };

  const closeSdsViewer = () => {
    if (sdsViewer?.url) window.URL.revokeObjectURL(sdsViewer.url);
    setSdsViewer(null);
  };

  const handleSdsDownloadFromViewer = () => {
    if (!sdsViewer) return;
    const a = document.createElement('a');
    a.href = sdsViewer.url;
    a.download = sdsViewer.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleExportBinder = async () => {
    setError('');
    try {
      setBinderBusy(true);
      const res = await exportSdsBinder();
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SDS_Binder.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.status === 404
        ? 'No SDS files on file yet — upload at least one SDS first.'
        : 'Failed to export SDS binder');
    } finally {
      setBinderBusy(false);
    }
  };

  // Apply an SDS action to every chemical id in the group (or just the single id)
  const targetIds = (chem) => chem?.ids?.length ? chem.ids : (chem?.id ? [chem.id] : []);

  const handleSdsUpload = async (file) => {
    if (!sdsModalChem || !file) return;
    setSdsError('');
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setSdsError('File must be a PDF');
      return;
    }
    try {
      setSdsBusy(true);
      for (const id of targetIds(sdsModalChem)) {
        await uploadChemicalSds(id, file);
      }
      setSdsModalChem(null);
      fetchDashboard();
    } catch (err) {
      setSdsError(err.response?.data?.error || 'Upload failed');
    } finally {
      setSdsBusy(false);
    }
  };

  const handleSdsFetchFromSupplier = async () => {
    if (!sdsModalChem) return;
    setSdsError('');
    try {
      setSdsBusy(true);
      for (const id of targetIds(sdsModalChem)) {
        await fetchChemicalSdsFromSupplier(id);
      }
      setSdsModalChem(null);
      fetchDashboard();
    } catch (err) {
      setSdsError(err.response?.data?.error || 'Could not fetch SDS from supplier');
    } finally {
      setSdsBusy(false);
    }
  };

  const handleSdsRemove = async () => {
    if (!sdsModalChem) return;
    if (!window.confirm('Remove the SDS for this chemical?')) return;
    setSdsError('');
    try {
      setSdsBusy(true);
      for (const id of targetIds(sdsModalChem)) {
        await deleteChemicalSds(id);
      }
      setSdsModalChem(null);
      fetchDashboard();
    } catch (err) {
      setSdsError(err.response?.data?.error || 'Failed to remove SDS');
    } finally {
      setSdsBusy(false);
    }
  };

  const handleReorder = async (location, newList) => {
    const order = newList.map(s => s.chemical.id);
    try {
      if (location === 'SS') {
        setDashboard(prev => ({ ...prev, ss_chemicals: newList }));
      } else {
        setDashboard(prev => ({ ...prev, auto_chemicals: newList }));
      }
      await reorderChemicals(location, order);
    } catch (err) {
      setError('Failed to reorder');
      fetchDashboard();
    }
  };

  const sortChemicals = (chemicals) => {
    if (sortMode === 'manual') return chemicals;
    const sorted = [...chemicals];
    if (sortMode === 'days_remaining') {
      sorted.sort((a, b) => {
        // Nulls (no data) go to bottom
        if (a.days_remaining === null && b.days_remaining === null) return 0;
        if (a.days_remaining === null) return 1;
        if (b.days_remaining === null) return -1;
        return a.days_remaining - b.days_remaining;
      });
    } else if (sortMode === 'name') {
      sorted.sort((a, b) => a.chemical.name.localeCompare(b.chemical.name));
    }
    return sorted;
  };

  // Order against grouped soaps, not raw rows, so a soap shared between the
  // two systems isn't credited with its stock twice and ordered for twice.
  // `covered` is the stock on hand expressed in days — the same figure the
  // Soap Inventory table shows as Days Supply.
  const calculateOrder = (group) => {
    if (!group.daysPerJug) return { jugs: null, cost: null, covered: null };
    const covered = Math.max(0, group.daysSupply ?? 0);
    const shortfallDays = Math.max(0, orderDays - covered);
    const jugs = Math.ceil(shortfallDays / group.daysPerJug);
    const cost = jugs * group.costPer;
    return { jugs, cost, covered };
  };

  const exportOrder = () => {
    const groups = combineSharedSoaps([
      ...(dashboard?.ss_chemicals || []),
      ...(dashboard?.auto_chemicals || []),
    ]);
    const periodLabel = { 90: '3 Months', 180: '6 Months', 270: '9 Months', 365: '12 Months' }[orderDays] || `${orderDays} Days`;

    const rows = groups.map(group => {
      const order = calculateOrder(group);
      return {
        'Chemical': group.name,
        'System': group.system,
        'Supplier': group.entries[0].chemical.supplier,
        'Container Size (gal)': group.size,
        'Days Supply On Hand': order.covered !== null ? Math.round(order.covered) : '',
        'Qty Needed': order.jugs !== null ? order.jugs : '',
        'Cost per Jug': group.costPer,
        'Line Total': order.cost !== null ? Math.round(order.cost * 100) / 100 : '',
        'Product Link': group.productUrl
      };
    });

    const grandTotal = groups.reduce((sum, g) => sum + (calculateOrder(g).cost || 0), 0);

    rows.push({
      'Chemical': '',
      'System': '',
      'Supplier': '',
      'Container Size (gal)': '',
      'Days Supply On Hand': '',
      'Qty Needed': '',
      'Cost per Jug': 'TOTAL:',
      'Line Total': Math.round(grandTotal * 100) / 100,
      'Product Link': ''
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, { wch: 12 }, { wch: 16 }, { wch: 18 },
      { wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 50 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Chemical Order');

    // Add a header row as a separate sheet with order info
    const infoRows = [
      { 'Field': 'Order Date', 'Value': new Date().toLocaleDateString() },
      { 'Field': 'Coverage Period', 'Value': periodLabel },
      { 'Field': 'Estimated Total', 'Value': `$${grandTotal.toFixed(2)}` },
      { 'Field': 'Business', 'Value': 'Winborne Wheels Car Wash' }
    ];
    const infoWs = XLSX.utils.json_to_sheet(infoRows);
    infoWs['!cols'] = [{ wch: 20 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, infoWs, 'Order Info');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const dateStr = new Date().toISOString().split('T')[0];
    saveAs(blob, `Chemical_Order_${periodLabel.replace(' ', '_')}_${dateStr}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const allChemicals = [...(dashboard?.ss_chemicals || []), ...(dashboard?.auto_chemicals || [])];
  const hasChemicals = allChemicals.length > 0;
  const orderGroups = combineSharedSoaps(allChemicals);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Soap Tracking</h1>
          <div className="hidden sm:flex items-center space-x-3">
            {refreshMsg && (
              <span className="text-sm text-gray-600">{refreshMsg}</span>
            )}
            <button
              onClick={handleRefreshPrices}
              disabled={refreshing}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {refreshing ? 'Refreshing...' : 'Refresh Prices'}
            </button>
            <Link
              to="/inventory/log"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Log Soap Change
            </Link>
            <Link
              to="/inventory/chemical/new"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + Add Chemical
            </Link>
          </div>
        </div>
        {refreshMsg && (
          <p className="sm:hidden text-sm text-gray-600 mt-2">{refreshMsg}</p>
        )}
        <div className="sm:hidden grid grid-cols-3 gap-1.5 mt-3">
          <button
            onClick={handleRefreshPrices}
            disabled={refreshing}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-1 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 text-center flex items-center justify-center"
          >
            {refreshing ? 'Refreshing...' : 'Refresh Prices'}
          </button>
          <Link
            to="/inventory/log"
            className="bg-green-600 hover:bg-green-700 text-white px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center"
          >
            Log Soap Change
          </Link>
          <Link
            to="/inventory/chemical/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center"
          >
            + Add Chemical
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Low Stock Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">Low Stock Alerts</h2>
          <div className="space-y-1">
            {dashboard.alerts.map((alert, i) => (
              <div key={i} className="text-sm text-yellow-700">
                <span className="font-medium">{alert.chemical}</span>
                <span className="text-yellow-500 ml-1">({alert.location})</span>
                {' '}&mdash; ~{Math.max(Math.round(alert.days_until_empty), 0)} days of supply remaining
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasChemicals ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No chemicals added yet.{' '}
          <Link to="/inventory/chemical/new" className="text-blue-600 hover:text-blue-800">
            Add your first chemical
          </Link>
        </div>
      ) : (
        <>
          {/* Sort Controls */}
          {/* Desktop: buttons */}
          <div className="hidden sm:flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            {[
              { value: 'manual', label: 'Custom Order' },
              { value: 'days_remaining', label: 'Days Remaining' },
              { value: 'name', label: 'Name' }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setSortMode(opt.value)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  sortMode === opt.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Mobile: dropdown */}
          <div className="sm:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="manual">Custom Order</option>
              <option value="days_remaining">Days Remaining</option>
              <option value="name">Name</option>
            </select>
          </div>

          {/* SS Bays Section */}
          <ChemicalSection
            title="SS Bays"
            subtitle="All 4 bays share these chemicals"
            location="SS"
            chemicals={sortChemicals(dashboard?.ss_chemicals || [])}
            onDelete={handleDelete}
            onReorder={sortMode === 'manual' ? handleReorder : null}
          />

          {/* Auto Wash Section */}
          <ChemicalSection
            title="Auto Wash"
            subtitle="WashWorld chemicals"
            location="Auto"
            chemicals={sortChemicals(dashboard?.auto_chemicals || [])}
            onDelete={handleDelete}
            onReorder={sortMode === 'manual' ? handleReorder : null}
          />

          {/* Soap Inventory */}
          <SoapInventory
            chemicals={allChemicals}
            onUpdate={(id, qty) => {
              updateChemical(id, { quantity_on_hand: qty }).then(() => fetchDashboard());
            }}
            onOpenSds={(group) => { setSdsError(''); setSdsModalChem(group); }}
            onViewSds={handleSdsView}
            onExportBinder={handleExportBinder}
            binderBusy={binderBusy}
          />

          {/* Archived (retired) soaps — kept so their change history and cost
              stay attached to the periods they were used in. */}
          {dashboard?.archived_chemicals?.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Archived Soaps</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Off the active slate — excluded from ordering, alerts and current soap cost,
                  but their change history still counts toward the periods they were used in.
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {dashboard.archived_chemicals.map(chem => (
                  <div key={chem.id} className="px-6 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {chem.name}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {chem.location === 'SS' ? 'SS Bays' : 'Auto Wash'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {chem.total_changes} logged change{chem.total_changes === 1 ? '' : 's'}
                        {chem.archived_at && ` · archived ${new Date(chem.archived_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRestore(chem.id, chem.name)}
                      className="shrink-0 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Soap Changes */}
          {dashboard?.recent_changes?.length > 0 && (
            <RecentChanges
              initialChanges={dashboard.recent_changes}
              onRefresh={fetchDashboard}
            />
          )}

          {/* Order Calculator */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Order Calculator</h2>
              <button
                onClick={exportOrder}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Export to Excel
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <label className="text-sm font-medium text-gray-700">Coverage period:</label>
                <select
                  value={orderDays}
                  onChange={(e) => setOrderDays(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={90}>3 months (90 days)</option>
                  <option value={180}>6 months (180 days)</option>
                  <option value={270}>9 months (270 days)</option>
                  <option value={365}>12 months (365 days)</option>
                </select>
              </div>

              <ScrollHint>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chemical</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Supply On Hand</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jugs Needed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost/Jug</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderGroups.map(group => {
                      const order = calculateOrder(group);
                      return (
                        <tr key={group.key} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {group.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {group.system}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {order.covered !== null ? `~${Math.round(order.covered)} days` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.jugs !== null ? order.jugs : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${group.costPer.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {order.cost !== null ? `$${order.cost.toFixed(2)}` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {group.productUrl ? (
                              <a
                                href={group.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Order
                              </a>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Grand Total */}
                    <tr className="bg-gray-50 font-bold">
                      <td colSpan="5" className="px-6 py-4 text-sm text-gray-900 text-right">
                        Grand Total:
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${orderGroups
                          .reduce((sum, group) => sum + (calculateOrder(group).cost || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </ScrollHint>
            </div>
          </div>
        </>
      )}

      {retireTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Retire {retireTarget.name}</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                If this soap has any logged changes it's archived rather than deleted — the
                history and cost stay on the periods it was used in, and you can restore it later.
                It drops out of ordering, low-stock alerts and soap cost from the date below.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last day in service
                </label>
                <input
                  type="date"
                  value={retireDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setRetireDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ minHeight: '42px' }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Back-date this if you actually stopped using it a while ago, so the months
                  since aren't charged for it.
                </p>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setRetireTarget(null)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmRetire}
                disabled={retiring || !retireDate}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium"
              >
                {retiring ? 'Retiring…' : 'Retire soap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sdsViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl flex flex-col" style={{ width: '90vw', height: '90vh', maxWidth: '1100px' }}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 truncate">SDS — {sdsViewer.name}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSdsDownloadFromViewer}
                  className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Download
                </button>
                <button
                  onClick={closeSdsViewer}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
            <iframe
              src={sdsViewer.url}
              title={`SDS for ${sdsViewer.name}`}
              className="flex-1 w-full"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}

      {sdsModalChem && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              SDS — {sdsModalChem.name}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {sdsModalChem.has_sds
                ? `On file: ${sdsModalChem.sds_filename || 'sds.pdf'}${sdsModalChem.sds_uploaded_at ? ` (uploaded ${new Date(sdsModalChem.sds_uploaded_at).toLocaleDateString()})` : ''}`
                : 'No SDS on file yet.'}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload PDF
            </label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={sdsBusy}
              onChange={(e) => handleSdsUpload(e.target.files?.[0])}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />

            {sdsModalChem.product_url && (
              <button
                onClick={handleSdsFetchFromSupplier}
                disabled={sdsBusy}
                className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm disabled:opacity-50"
              >
                {sdsBusy ? 'Fetching…' : 'Fetch SDS from supplier page'}
              </button>
            )}

            {sdsError && <p className="mt-3 text-sm text-red-600">{sdsError}</p>}

            <div className="mt-5 flex items-center justify-between">
              {sdsModalChem.has_sds ? (
                <button
                  onClick={handleSdsRemove}
                  disabled={sdsBusy}
                  className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                >
                  Remove SDS
                </button>
              ) : <span />}
              <button
                onClick={() => setSdsModalChem(null)}
                disabled={sdsBusy}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ChemicalSection = ({ title, subtitle, location, chemicals, onDelete, onReorder }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  if (chemicals.length === 0) return null;

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    if (dragIndex !== null && overIndex !== null && dragIndex !== overIndex) {
      const newList = [...chemicals];
      const [dragged] = newList.splice(dragIndex, 1);
      newList.splice(overIndex, 0, dragged);
      onReorder(location, newList);
    }
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(idx);
  };

  const isDraggable = Boolean(onReorder);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      <ScrollHint>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isDraggable && (
                <th className="w-10 px-2 py-3"></th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chemical</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Days/Jug</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Changed</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Remaining</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chemicals.map((stats, idx) => (
              <tr
                key={stats.chemical.id}
                draggable={isDraggable}
                onDragStart={isDraggable ? (e) => handleDragStart(e, idx) : undefined}
                onDragEnd={isDraggable ? handleDragEnd : undefined}
                onDragOver={isDraggable ? (e) => handleDragOver(e, idx) : undefined}
                className={`
                  hover:bg-gray-50
                  ${stats.low_stock ? 'bg-yellow-50' : ''}
                  ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}
                  ${overIndex === idx && dragIndex !== null && dragIndex !== idx ? 'border-t-2 border-blue-400' : ''}
                `}
              >
                {isDraggable && (
                  <td className="w-10 px-2 py-4 text-center text-gray-300 select-none">
                    <span title="Drag to reorder">&#9776;</span>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {stats.chemical.name}
                    {stats.low_stock && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Low
                      </span>
                    )}
                    {stats.chemical.product_url && (
                      <a
                        href={stats.chemical.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-600"
                        title="View on Kleen-Rite"
                      >
                        &#8599;
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {stats.chemical.container_size_gallons} gal
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${stats.chemical.cost_per_container.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {stats.avg_days_per_jug !== null ? stats.avg_days_per_jug : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {stats.last_changed
                    ? new Date(stats.last_changed + 'T12:00:00').toLocaleDateString()
                    : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {stats.days_remaining !== null && stats.days_remaining > 0 ? (
                    <span className={stats.days_remaining <= 14
                      ? 'text-yellow-600 font-medium'
                      : 'text-green-600'
                    }>
                      ~{stats.days_remaining} days
                    </span>
                  ) : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {stats.total_changes}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                  <Link
                    to={`/inventory/chemical/edit/${stats.chemical.id}`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => onDelete(stats.chemical.id, stats.chemical.name)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollHint>
    </div>
  );
};

const SoapInventory = ({ chemicals, onUpdate, onOpenSds, onViewSds, onExportBinder, binderBusy }) => {

  const fmt = (val) => '$' + Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const sdsFieldsForGroup = (entries) => {
    const withSds = entries.find(e => e.chemical.has_sds);
    return {
      hasSds: !!withSds,
      sdsFilename: withSds?.chemical.sds_filename || null,
      sdsUploadedAt: withSds?.chemical.sds_uploaded_at || null,
      sdsDownloadId: withSds?.chemical.id || null,
      productUrl: entries[0].chemical.product_url || '',
    };
  };

  // One row per soap, sharing the same combining rules the order calculator
  // uses so the two tables can't drift apart.
  const grouped = combineSharedSoaps(chemicals).map(group => ({
    ...group,
    ...sdsFieldsForGroup(group.entries),
  }));

  const anySds = grouped.some(r => r.hasSds);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Soap Inventory</h2>
          <p className="text-sm text-gray-500">Current stock on hand</p>
        </div>
        <button
          onClick={onExportBinder}
          disabled={!anySds || binderBusy}
          className="px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          title={anySds ? 'Compile every SDS on file into a single PDF binder' : 'Upload at least one SDS to enable'}
        >
          {binderBusy ? 'Building binder…' : 'Export SDS Binder'}
        </button>
      </div>
      <ScrollHint>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Soap</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Supply</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SDS</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sr-only">Edit</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {grouped.map(row => {
              const totalCost = row.qty * row.costPer;
              return (
                <tr key={row.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {row.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {row.size} gal
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {row.system}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onUpdate(row.ids[0], Math.max(0, row.qty - 1))}
                        className="w-7 h-7 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-sm"
                      >
                        &minus;
                      </button>
                      <span className="w-6 text-center font-medium">{row.qty}</span>
                      <button
                        onClick={() => onUpdate(row.ids[0], row.qty + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-sm"
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.daysSupply !== null && row.daysSupply !== undefined
                      ? <span className={row.daysSupply < 30 ? 'text-red-600 font-medium' : ''}>{Math.round(row.daysSupply)} days</span>
                      : <span className="text-gray-400">N/A</span>
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {row.qty > 0 ? fmt(totalCost) : <span className="text-gray-400">$0.00</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {row.hasSds ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onViewSds && onViewSds({ id: row.sdsDownloadId, name: row.name, sds_filename: row.sdsFilename })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                        <button
                          onClick={() => onOpenSds && onOpenSds({
                            id: row.ids[0], ids: row.ids, name: row.name,
                            has_sds: row.hasSds, sds_filename: row.sdsFilename,
                            sds_uploaded_at: row.sdsUploadedAt, product_url: row.productUrl,
                          })}
                          className="text-gray-500 hover:text-gray-700 text-xs"
                          title="Replace or remove this SDS"
                        >
                          (manage)
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onOpenSds && onOpenSds({
                          id: row.ids[0], ids: row.ids, name: row.name,
                          has_sds: false, sds_filename: null,
                          sds_uploaded_at: null, product_url: row.productUrl,
                        })}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Upload
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/inventory/chemical/edit/${row.ids[0]}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              );
            })}
            {/* Totals row */}
            <tr className="bg-gray-50 font-bold">
              <td colSpan="3" className="px-6 py-4 text-sm text-gray-900 text-right">Total:</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {grouped.reduce((sum, r) => sum + r.qty, 0)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {fmt(grouped.reduce((sum, r) => sum + (r.qty * r.costPer), 0))}
              </td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </ScrollHint>
    </div>
  );
};

const RecentChanges = ({ initialChanges, onRefresh }) => {
  const defaultStart = new Date(Date.now() - 183 * 86400000).toISOString().split('T')[0];
  const defaultEnd = new Date().toISOString().split('T')[0];

  const [collapsed, setCollapsed] = useState(true);
  const [changes, setChanges] = useState(initialChanges);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [chemicalFilter, setChemicalFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editBottles, setEditBottles] = useState(1);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Unique chemical names for the filter dropdown
  const chemicalNames = [...new Set(changes.map(c => c.chemical_name))].sort();

  // Apply chemical filter
  const filteredChanges = chemicalFilter
    ? changes.filter(c => c.chemical_name === chemicalFilter)
    : changes;

  const fetchChanges = async (start, end) => {
    try {
      const data = await getAllChemicalChanges({ start_date: start, end_date: end });
      setChanges(data.changes);
    } catch {
      // keep existing data on failure
    }
  };

  const handleStartChange = (val) => {
    setStartDate(val);
    fetchChanges(val, endDate);
  };

  const handleEndChange = (val) => {
    setEndDate(val);
    fetchChanges(startDate, val);
  };

  const refreshLocal = () => {
    fetchChanges(startDate, endDate);
    onRefresh();
  };

  const startEdit = (change) => {
    setEditingId(change.id);
    setEditDate(change.date);
    setEditBottles(change.bottles || 1);
    setEditNotes(change.notes || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      await updateChemicalChange(editingId, {
        date: editDate,
        bottles: Math.max(1, parseInt(editBottles, 10) || 1),
        notes: editNotes,
      });
      setEditingId(null);
      refreshLocal();
    } catch {
      // keep editing on failure
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (change) => {
    if (!window.confirm(`Delete this ${change.chemical_name} change from ${new Date(change.date + 'T12:00:00').toLocaleDateString()}?`)) return;
    try {
      await deleteChemicalChange(change.id);
      refreshLocal();
    } catch {
      // silently fail
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-6 py-4 border-b border-gray-200 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Recent Soap Changes</h2>
          <p className="text-sm text-gray-500">{filteredChanges.length} changes in selected range</p>
        </div>
        <span className="text-gray-400 text-lg">{collapsed ? '\u25B6' : '\u25BC'}</span>
      </button>
      {!collapsed && <>
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleStartChange(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleEndChange(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <label className="text-sm font-medium text-gray-700 ml-2">Chemical:</label>
          <select
            value={chemicalFilter}
            onChange={(e) => setChemicalFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All</option>
            {chemicalNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <ScrollHint>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chemical</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bottles</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredChanges.map(change => (
              <tr key={change.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {editingId === change.id ? (
                    <input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    new Date(change.date + 'T12:00:00').toLocaleDateString()
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {change.chemical_name}
                  {change.chemical_archived && (
                    <span className="ml-2 text-xs font-normal text-gray-400">(retired)</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {change.chemical_location === 'SS' ? 'SS Bays' : 'Auto Wash'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {editingId === change.id ? (
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={editBottles}
                      onChange={(e) => setEditBottles(e.target.value)}
                      className="w-20 border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    (change.bottles || 1) > 1
                      ? <span className="font-medium text-gray-900">{change.bottles}</span>
                      : <span className="text-gray-400">1</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                  {editingId === change.id ? (
                    <input
                      type="text"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      className="w-full border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="Notes..."
                    />
                  ) : (
                    change.notes || <span className="text-gray-300">&mdash;</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                  {editingId === change.id ? (
                    <>
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(change)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(change)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollHint>
      </>}
    </div>
  );
};

export default ChemicalDashboard;
